/**
 * Keepsy Konfigurator: Controller der Seite konfigurator.html.
 *
 * Drei Schritte ohne Seitenwechsel und ohne Modal (RESEARCH 3.1, 3.2): Handy, Foto, Anpassen.
 * Seit 05.10.2026 (Louis) gibt es nur noch den Foto-Weg: kein Umschalter "Eigenes Foto | Mit Namen", keine
 * Namens-Designs im Konfigurator. Schritt 2 hat Foto hochladen oder tauschen, "Foto hinzufügen" (weitere Fotos als
 * eigene Ebenen), "Name dazu" (ein freies Textfeld, kein Design) und die Kaufbox. Looks und Collage stehen
 * eingeklappt in Schritt 3 und werden nie von selbst angewendet.
 * Browser-Zurück geht einen Schritt zurück (History API), Deep Links ?model=&name=&edit=&photo=&from=.
 * ?photo=<srcKey> (auch mehrfach) übernimmt Fotos aus IndexedDB (z. B. von der Startseite): ohne Modell warten sie,
 * bis das Handy gewählt ist. ?from=<Artikel> ("Gleiches Foto, anderes Handy") legt die ganze Gestaltung eines
 * Warenkorb-Artikels mit allen Foto-Ebenen auf das neue Handy. Alte Links mit ?mode=name oder ?design= laufen in den
 * Foto-Weg (die Parameter werden ignoriert), ?name= wird zum Textfeld "Name dazu".
 * Die Vorschau bleibt stehen, nur das Panel wechselt. Mobil wird die klebende Vorschau kleiner, sobald das Panel
 * darunter durchscrollt. Die Engine (engine.js) ist die Wahrheit, alle Bausteine spiegeln ihren Zustand:
 *   model-picker.js  Schritt 1, Combobox
 *   ui-photo.js      Schritt 2, Upload, Foto hinzufügen, Verlauf; Schritt 3, Collage-Felder
 *   gallery.js       Schritt 3, Looks und Collage-Layouts
 *   tools.js         Werkzeugleiste unter der Vorschau (gewähltes oder zuletzt gewähltes Foto), Schritt 3, Ebenen
 *   ui-buy.js        Kaufbox, Kaufleiste, Export, Warenkorb
 * Dazu: Entwurf in store.setDraft (Wiederkehr-Leiste), Bearbeiten aus dem Warenkorb (?edit=), Hinweise unter
 * der Vorschau, Ansagen über genau eine Status- und eine Alert-Region, Tastenkürzel-Hilfe (Taste ?).
 * Mit ?debug=1: Buttons "Testbild" und window.__keepsyConfigurator.
 *
 * Shopify-Theme: Das Markup kommt aus sections/main-configurator.liquid (Produkt-Template, /products/handyhuelle).
 * Die Section übergibt die Ausführungen Standard und Magnetic (früher MagSafe) mit echten Preisen als JSON (configurator/shop.js),
 * Deep Links und ?edit=<line key> laufen dort genauso. Ohne Konfigurator-Markup macht das Modul nichts.
 * @module configurator/index
 */

import { store } from '@keepsy/store.js'
import { cartBackend } from '@keepsy/cart-backend.js'
import { loadJson, DATA_PATHS, getPhoneModels } from '@keepsy/data.js'
import { getFile } from '@keepsy/db.js'
import { debounce, escapeHtml } from '@keepsy/format.js'
import { renderSilhouette, renderPhotoMockup, sanitizeName, ensureFonts } from '@keepsy/design-renderer.js'
import { getHeaderSlot } from '@keepsy/ui-header.js'
import { icon } from '@keepsy/ui-icons.js'
import { toast } from '@keepsy/ui-toast.js'
import { createEngine, BASE_BACKGROUND, LEGACY_PHOTO_DESIGN, LIMITS, NAME_FIELD } from '@keepsy/configurator-engine.js'
import { createModelPicker } from '@keepsy/configurator-model-picker.js'
import { createGallery, createCollagePicker, bindFold } from '@keepsy/configurator-gallery.js'
import { createTools, colorNamer } from '@keepsy/configurator-tools.js'
import { createPhotoPanel } from '@keepsy/configurator-ui-photo.js'
import { createBuy, hasContent } from '@keepsy/configurator-ui-buy.js'
import { readShopProduct } from '@keepsy/configurator-shop.js'

/** Kundenschriften (data.js kennt die Datei nicht). */
const FONTS_PATH = 'data/fonts.json'

const STEP_LABELS = ['Handy', 'Foto', 'Anpassen']

/** Erlaubte Form eines srcKey aus dem Link (?photo=), wie db.js-Keys aus uid(). */
const PHOTO_KEY = /^[A-Za-z0-9_-]{1,80}$/

/** Erlaubte Form einer Artikel-ID aus dem Link (?from=), Prototyp ci_... oder Shopify-Zeilenschlüssel. */
const ITEM_KEY = /^[A-Za-z0-9_:.-]{1,120}$/

const main = document.querySelector('[data-component="configurator"]')
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)')
const desktopMq = matchMedia('(min-width: 64rem)')

/* ========================================================================== */
/* Ansagen: genau eine Status- und eine Alert-Region (a11y 3.3)               */
/* ========================================================================== */

const statusEl = main ? main.querySelector('[data-cfg-status]') : null
const alertEl = main ? main.querySelector('[data-cfg-alert]') : null
let lastStatus = ''
let statusTimer = 0

/**
 * Ansage über role="status". Gleiche Ansage zweimal hintereinander wird minimal verändert, sonst schweigt der Screenreader.
 * @param {string} text
 * @param {number} [delay=0]
 */
function announce(text, delay = 0) {
  if (!text) return
  clearTimeout(statusTimer)
  statusTimer = setTimeout(() => {
    let value = text
    if (value === lastStatus) value += ' '
    lastStatus = value
    statusEl.textContent = ''
    setTimeout(() => {
      statusEl.textContent = value
    }, 40)
  }, delay)
}

/**
 * Blockierende Fehler über role="alert".
 * @param {string} text
 */
function alertText(text) {
  alertEl.textContent = ''
  setTimeout(() => {
    alertEl.textContent = text
  }, 40)
}

/* ========================================================================== */
/* Start                                                                       */
/* ========================================================================== */

// Ohne Konfigurator-Markup (andere Seite, Theme-Editor ohne Section) bleibt das Modul still
if (main) {
  boot().catch((err) => {
    console.error('[keepsy/configurator] Start fehlgeschlagen.', err)
    const panel = main.querySelector('[data-cfg-panel]')
    if (panel) {
      const p = document.createElement('p')
      p.className = 'k-hint k-hint--danger'
      p.setAttribute('role', 'alert')
      p.innerHTML = `${icon('alert', { size: 20 })}<span><span class="k-hint__label">Fehler:</span> Der Konfigurator konnte nicht starten. Lade die Seite neu, dein Entwurf bleibt erhalten.</span>`
      panel.prepend(p)
    }
  })
}

/**
 * Wartet, bis das Dokument geparst ist. Dann sind alle Module gelaufen, auch app.js mit Kopf und Drawer
 * (im Shopify-Theme steht app.js im Layout hinter der Section, im Prototyp davor).
 * @returns {Promise<void>}
 */
function domReady() {
  if (document.readyState !== 'loading') return Promise.resolve()
  return new Promise((resolve) => document.addEventListener('DOMContentLoaded', () => resolve(), { once: true }))
}

/**
 * Zwei Hintergründe gleich (für "Ohne Look": eine selbst gewählte Grundfarbe bleibt, die des Looks nicht)?
 * @param {Object | null} a
 * @param {Object | null} b
 */
function sameBackground(a, b) {
  if (!a || !b || a.type !== b.type) return false
  if (a.type === 'solid') return String(a.color || '').toUpperCase() === String(b.color || '').toUpperCase()
  if (a.type === 'linear') return JSON.stringify((a.colors || []).map((c) => String(c).toUpperCase())) === JSON.stringify((b.colors || []).map((c) => String(c).toUpperCase()))
  return true
}

async function boot() {
  const [models, designsJson, stickersJson, fontsJson] = await Promise.all([
    getPhoneModels(),
    loadJson(DATA_PATHS.designs),
    loadJson(DATA_PATHS.stickers).catch(() => ({})),
    loadJson(FONTS_PATH).catch(() => ({ fonts: [] })),
    domReady(),
  ])
  const designs = Array.isArray(designsJson.designs) ? designsJson.designs : []
  const meta = designsJson.meta || {}
  const modelsById = new Map(models.map((m) => [m.id, m]))
  // Frühere IDs zusammengelegter Modelle (zum Beispiel iphone-17e für iPhone 16e / 17e)
  for (const m of models) for (const alias of m.aliases || []) if (!modelsById.has(alias)) modelsById.set(alias, m)
  const designsById = new Map(designs.map((d) => [d.id, d]))
  const sortPopular = (list) => [...list].sort((a, b) => Number(Boolean(b.popular)) - Number(Boolean(a.popular)))
  // Looks: Foto-Designs aus data/designs.json. Die erste Karte heißt "Ohne Look" (freie Fotos, kein Rahmen).
  const noLook = designsById.get(LEGACY_PHOTO_DESIGN)
  const looks = [
    ...(noLook ? [{ ...noLook, name: 'Ohne Look' }] : []),
    ...sortPopular(designs.filter((d) => d.supports && d.supports.photo && d.id !== LEGACY_PHOTO_DESIGN)),
  ]
  const palette = meta.palette || {}
  const nameOfColor = colorNamer(palette)
  const fonts = Array.isArray(fontsJson.fonts) ? fontsJson.fonts : []

  /* ------------------------------ Zustand ------------------------------ */

  const params = new URLSearchParams(location.search)
  const debug = params.has('debug')
  const photoParams = [...new Set(params.getAll('photo').filter((k) => PHOTO_KEY.test(k)))].slice(0, LIMITS.photos)
  const photoParam = photoParams[0] || null
  const fromParam = ITEM_KEY.test(params.get('from') || '') ? params.get('from') : null
  const state = {
    step: 1,
    model: null,
    magsafe: false,
    editId: null,
    /** Aus dem Link, bis das Handy gewählt ist: { name } */
    pending: null,
    /** srcKey eines Fotos aus dem Link, das auf die Modellwahl wartet. */
    pendingPhoto: null,
    /** Weitere Fotos aus dem Link, sie kommen als eigene Ebenen dazu. */
    pendingPhotos: [],
    /** Artikel ("Gleiches Foto, anderes Handy"), dessen Gestaltung nach der Modellwahl auf die Hülle kommt. */
    pendingFrom: null,
    uploads: [],
    recentModels: [],
    modelRequests: [],
    booting: true,
    keyboardOpen: false,
    hintDismissed: false,
    /** Feld "Name dazu" aufgeklappt. */
    nameOpen: false,
  }

  /* ------------------------------ DOM ------------------------------ */

  const $ = (sel) => main.querySelector(sel)
  const panel = $('[data-cfg-panel]')
  const stageBox = $('[data-cfg-stage-box]')
  const stageEl = $('[data-cfg-stage]')
  const silhouetteEl = $('[data-cfg-silhouette]')
  const stick = $('[data-cfg-stick]')
  const contextEl = $('[data-cfg-context]')
  const hintsEl = $('[data-cfg-hints]')
  const belowEl = $('[data-cfg-below]')
  const describeBtn = $('[data-action="describe"]')
  const describeEl = $('[data-cfg-describe]')
  const draftBar = $('[data-cfg-draft]')
  const draftText = $('[data-cfg-draft-text]')
  const storageHint = $('[data-cfg-storage-hint]')
  const stepper = $('[data-cfg-stepper]')
  const stepPanels = [...main.querySelectorAll('[data-step-panel]')]
  const nameAddonBtn = $('[data-action="add-name"]')
  const nameBody = $('[data-cfg-name-body]')
  const nameInput = $('[data-cfg-name]')
  const nameHint = $('[data-cfg-name-hint]')
  const collageOff = $('[data-action="collage-off"]')
  const asideEl = $('[data-cfg-aside]')
  const asideSlot = $('[data-cfg-aside-slot]')
  const hintsSlot = $('[data-cfg-hints-slot]')
  const buyEl = $('[data-cfg-buy]')
  const ctaEl = $('[data-cfg-cta]')
  const magsafeModal = document.querySelector('[data-cfg-magsafe-modal]')
  const keysModal = document.querySelector('[data-cfg-keys-modal]')

  /* ------------------------------ Entwurf: Listen übernehmen ------------------------------ */

  const draft = store.getDraft()
  if (draft && draft.v === 1) {
    if (Array.isArray(draft.uploads)) state.uploads = draft.uploads.filter((u) => u && typeof u.srcKey === 'string').slice(0, 12)
    if (Array.isArray(draft.recentModels)) state.recentModels = draft.recentModels.filter((id) => modelsById.has(id)).slice(0, 6)
    if (Array.isArray(draft.modelRequests)) state.modelRequests = draft.modelRequests.slice(-10)
    state.hintDismissed = Boolean(draft.hintDismissed)
  }

  /* ------------------------------ Engine ------------------------------ */

  let tools = null
  let photo = null
  let buy = null
  let photoGallery = null
  let collagePicker = null
  let lastPhotoKeys = ''
  let lastGalleryName = ''
  let lastWarningKeys = ''

  const engine = createEngine({
    container: stageEl,
    model: null,
    keyTarget: main,
    padding: 16,
    onChange: (config) => onEngineChange(config),
    onSelect: (sel) => onEngineSelect(sel),
    onWarning: (list) => renderWarnings(list),
    onEditText: (layer) => editText(layer),
    onAnnounce: (text) => announce(text),
    colorName: (hex) => nameOfColor(hex),
  })

  // Schriften für Galerie und Bühne (die Engine lädt sie auch, ensureFonts ist mehrfach sicher)
  ensureFonts(fontsJson).then(() => {
    photoGallery && photoGallery.update({ immediate: true })
  })

  /* ------------------------------ Foto-Vorschauen (für Galerien) ------------------------------ */

  const previews = new Map()

  /**
   * Verkleinerte Vorschau eines gespeicherten Fotos (ImageBitmap), lädt bei Bedarf nach.
   * @param {string} srcKey
   * @returns {CanvasImageSource | null}
   */
  function getPreview(srcKey) {
    if (!srcKey) return null
    const hit = previews.get(srcKey)
    if (hit) return hit.bitmap
    const entry = { bitmap: null }
    previews.set(srcKey, entry)
    getFile(srcKey)
      .then((blob) => (blob ? createImageBitmap(blob, { resizeWidth: 720, resizeQuality: 'medium' }) : null))
      .then((bitmap) => {
        entry.bitmap = bitmap
        if (bitmap) {
          if (photoGallery) photoGallery.update({ immediate: true })
          if (collagePicker) collagePicker.update()
          // Foto aus dem Link wartet auf das Modell: die Silhouette zeigt es schon
          if (!state.model && state.pendingPhoto === srcKey) drawSilhouette()
        }
      })
      .catch(() => null)
    return null
  }

  /** srcKeys der Fotos: erst die der Hülle, dann der Verlauf (für mehrteilige Looks und Collagen). */
  function photoKeysForGallery(c) {
    const keys = []
    const push = (k) => k && !keys.includes(k) && keys.push(k)
    if (c.collage) c.collage.slots.forEach((s) => push(s.srcKey))
    c.layers
      .filter((l) => l.type === 'photo-slot' && l.srcKey)
      .sort((a, b) => (Number(a.slot) || 0) - (Number(b.slot) || 0))
      .forEach((l) => push(l.srcKey))
    c.layers.filter((l) => l.type === 'image' && l.srcKey).forEach((l) => push(l.srcKey))
    state.uploads.forEach((u) => push(u.srcKey))
    return keys.slice(0, 4)
  }

  /* ------------------------------ Looks und Collage (Schritt 3, nie automatisch) ------------------------------ */

  /** Leere Hülle im Foto-Weg: keine Ebenen, Grundfarbe Weiß. */
  const emptyConfig = () => ({ version: 1, mode: 'photo', layers: [], collage: null, background: { ...BASE_BACKGROUND } })

  /**
   * Look wählen. "Ohne Look" nimmt Look und Collage herunter, die Fotos liegen wieder frei auf der Hülle.
   * @param {Object} design
   */
  function pickLook(design) {
    if (!design || !state.model) return
    const c = engine.getConfig()
    if (design.id === LEGACY_PHOTO_DESIGN) {
      if (c.collage) {
        engine.setCollage(null)
        return
      }
      if (!c.designId) return
      const look = designsById.get(c.designId)
      // Eine selbst gewählte Grundfarbe bleibt, die des Looks geht mit. Der Name aus dem Look bleibt als freies Feld.
      const own = look && look.background && !sameBackground(look.background, c.background)
      engine.clearDesign({ background: own ? c.background : BASE_BACKGROUND, nameField: NAME_FIELD })
      return
    }
    if (c.designId === design.id && !c.collage) return
    // Der Name aus "Name dazu" zieht in den Namensplatz des Looks um (so wie die Karte in der Galerie)
    const withName = design.layers.some((l) => l.type === 'text' && (l.bind === 'name' || l.bind === 'initial'))
    engine.loadDesign(design, { name: lookName(c) })
    announce(
      `Look ${design.name} gewählt. Deine Fotos liegen in den Feldern${withName && lookName(c) ? ', dein Name steht im Look' : ''}. Rückgängig holt den Stand von vorher.`,
    )
  }

  /** Name für Looks: der Text aus "Name dazu", sonst der Name eines Looks (alte Entwürfe). */
  function lookName(c) {
    const field = nameLayer(c)
    const text = field ? sanitizeName(field.text || '') : ''
    return text || c.name || ''
  }

  photoGallery = createGallery({
    root: $('[data-cfg-gallery="photo"]'),
    designs: looks,
    silentIds: [LEGACY_PHOTO_DESIGN],
    getParams: () => {
      const c = engine.getConfig()
      return { name: lookName(c), model: state.model, photos: photoKeysForGallery(c).map(getPreview).filter(Boolean) }
    },
    onPick: (design) => pickLook(design),
    // Kurz, weil es die Kopfzeile des ausklappbaren Bereichs ist (ist ein Look aktiv, steht dort sein Name)
    texts: ({ hasPhoto: has }) =>
      has
        ? { title: 'Looks', lead: 'Sofortbild, Herz, Fotostreifen. Optional, deine Fotos wandern mit.' }
        : { title: 'Looks', lead: 'Lad zuerst ein Foto hoch, dann zeigt jeder Look dein Bild.' },
  })

  collagePicker = createCollagePicker({
    root: $('[data-cfg-collage]'),
    getParams: () => {
      const c = engine.getConfig()
      return { model: state.model, photoKeys: photoKeysForGallery(c), background: { ...BASE_BACKGROUND } }
    },
    getImage: (key) => getPreview(key),
    onPick: (layout) => {
      if (!state.model) return
      const c = engine.getConfig()
      if (c.collage && c.collage.layout === layout) return
      engine.setCollage({ layout, gap: 0.02, radius: 0.03 })
    },
  })

  // Looks und Collage sind ausklappbar und starten zu. Beim Aufklappen in voller Breite neu zeichnen
  // (zugeklappt kennen die Karten ihre Breite nicht).
  bindFold($('[data-cfg-gallery="photo"]'), { onOpen: () => photoGallery.update({ immediate: true }) })
  bindFold($('[data-cfg-collage]'), { onOpen: () => collagePicker.update() })

  /* ------------------------------ Werkzeuge ------------------------------ */

  photo = createPhotoPanel({
    root: main,
    stageBox,
    engine,
    announce,
    alert: alertText,
    debug,
    onUploaded: (entry) => {
      state.uploads = [entry, ...state.uploads.filter((u) => u.srcKey !== entry.srcKey)].slice(0, 12)
      getPreview(entry.srcKey)
      saveDraft()
    },
    getUploads: () => state.uploads,
    getActivePhoto: () => activePhotoTarget(),
  })

  tools = createTools({
    engine,
    toolsRoot: $('[data-cfg-tools]'),
    contextEl,
    srLayersEl: $('[data-cfg-srlayers]'),
    data: { fonts, stickers: stickersJson, palette },
    announce,
    setName: (value, opts = {}) => {
      // Gebundene Namen gibt es nur noch in alten Entwürfen (Looks mit Namensfeld): direkt an die Engine
      if (opts.fromEngine) return
      engine.setName(value)
    },
    pickPhoto: (target) => {
      if (state.step !== 2 && !(target && (target.type === 'collage-slot' || target.type === 'photo-slot'))) goToStep(2, { focus: false })
      photo.pick(target)
    },
    requestTool: (name) => {
      if (state.step !== 3) goToStep(3, { focus: false })
      tools.openTool(name, { focus: true })
    },
    isDesktop: () => desktopMq.matches,
    getDesign: () => currentDesign(),
    hintDismissed: state.hintDismissed,
    onHintDismissed: () => {
      state.hintDismissed = true
      saveDraft()
    },
    onEditText: (layer) => editInline(layer),
  })

  buy = createBuy({
    panel,
    state,
    engine,
    designsById,
    // Shopify-Theme: Ausführungen Standard und Magnetic mit echten Preisen aus der Section (sonst null)
    shop: readShopProduct(main),
    announce,
    alert: alertText,
    onNeedModel: () => {
      goToStep(1)
      picker.focus()
    },
    onNeedContent: () => {
      if (state.step !== 2) goToStep(2, { focus: false })
      // "Erst Foto hochladen" macht genau das: Dateiauswahl sofort, solange der Tipp als Nutzeraktion zählt.
      // Der Upload-Bereich rückt ins Bild und hält den Fokus, wenn jemand die Auswahl abbricht.
      photo.pick(null)
      const uploadBtn = main.querySelector('[data-action="upload"]')
      uploadBtn.focus({ preventScroll: true })
      main.querySelector('[data-cfg-upload]').scrollIntoView({ block: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' })
      announce('Wähl ein Foto aus, dann geht es weiter.')
    },
    onStartAnother: () => startAnother(),
    onReplacePhoto: (target) => {
      if (state.step !== 2 && !(target && (target.type === 'collage-slot' || target.type === 'photo-slot'))) goToStep(2, { focus: false })
      photo.pick(target)
    },
    onBusy: (on) => main.classList.toggle('is-exporting', on),
    onChange: () => saveDraft(),
    onAdded: () => {
      markDraftAdded()
    },
    openMagsafeInfo: (opener) => openModal(magsafeModal, opener),
  })

  /** Foto, auf das "Anderes Foto" wirkt: gewähltes, sonst das der Werkzeugleiste, sonst das unterste. */
  function activePhotoTarget() {
    const sel = engine.getSelected()
    if (sel && (sel.type === 'image' || sel.type === 'collage-slot' || sel.type === 'photo-slot')) return sel
    const id = tools ? tools.photoTargetId() : null
    const layer = id ? engine.getLayer(id) : null
    if (layer) return layer
    const first = engine.getPhotoIds()[0]
    return first ? engine.getLayer(first) : null
  }

  const currentDesign = () => {
    const c = engine.getConfig()
    return c && c.designId && !c.collage ? designsById.get(c.designId) || null : null
  }

  /* ------------------------------ Modellwahl ------------------------------ */

  const picker = createModelPicker({
    root: $('[data-cfg-models]'),
    models,
    onSelect: (model) => selectModel(model),
    onContinue: () => goToStep(2),
    getRecentIds: () => [...state.recentModels, ...cartBackend.getCart().items.map((i) => i.modelId)],
    onMissing: (text) => {
      state.modelRequests.push({ text, at: new Date().toISOString() })
      saveDraft()
    },
    announce,
  })

  /**
   * Modell wählen. Beim ersten Mal ist die Hülle leer und weiß (kein Design). Wartet ein Foto oder eine Gestaltung
   * aus dem Link, liegt sie danach sofort auf der Hülle.
   * @param {Object} model
   * @param {{ silent?: boolean }} [opts]
   */
  function selectModel(model, opts = {}) {
    if (!model) return
    const prev = state.model
    state.model = model
    if (!model.magsafe) state.magsafe = false
    state.recentModels = [model.id, ...state.recentModels.filter((id) => id !== model.id)].slice(0, 6)
    picker.setSelected(model)
    showStage(true)
    renderPendingPhoto()
    if (!prev) {
      engine.setModel(model)
      const c = engine.getConfig()
      if (state.pendingFrom && applyPendingFrom()) {
        // Gestaltung aus dem Warenkorb liegt auf dem neuen Handy
      } else {
        if (!hasContent(c) && !c.collage && !c.designId) setupInitial()
        else refitDesign()
        if (state.pendingPhoto) applyPendingPhoto()
      }
    } else if (prev.id !== model.id) {
      engine.setModel(model)
      refitDesign()
    }
    renderHeader()
    photoGallery.update({ immediate: true })
    collagePicker.update()
    buy.render()
    // "Zuletzt gewählt" sofort aktuell halten, nicht erst nach einem Reload
    picker.refreshRecent()
    if (!opts.silent) saveDraft()
  }

  /** Erster Start auf dem Modell: leere, weiße Hülle. Ein Name aus dem Link wird zum Textfeld "Name dazu". */
  function setupInitial() {
    const p = state.pending || {}
    state.pending = null
    engine.load(emptyConfig())
    if (p.name) addNameField(p.name, { focus: false, select: false })
  }

  /**
   * Nach einem Modellwechsel den Look neu setzen, damit er der Kamera ausweicht wie in der Galerie.
   * Hintergrund und Namens-Stil, die jemand schon geändert hat, bleiben.
   */
  function refitDesign() {
    const before = engine.getConfig()
    const design = before.designId && !before.collage ? designsById.get(before.designId) : null
    if (!design) return
    const named = before.layers.find((l) => l.type === 'text' && l.bind === 'name')
    engine.loadDesign(design, { name: before.name })
    const after = engine.getConfig()
    let patched = false
    if (JSON.stringify(after.background) !== JSON.stringify(before.background)) {
      after.background = before.background
      patched = true
    }
    if (named) {
      for (const l of after.layers) {
        if (l.type !== 'text' || l.bind !== 'name') continue
        for (const key of ['font', 'weight', 'color']) {
          if (l[key] !== named[key] && named[key] !== undefined) {
            l[key] = named[key]
            patched = true
          }
        }
      }
    }
    if (patched) engine.load(after, { resetHistory: false })
  }

  /* ------------------------------ Foto aus dem Link (?photo=) ------------------------------ */

  const pendingNote = $('[data-cfg-pending-photo]')

  /** Hinweis in Schritt 1, solange ein Foto oder eine Gestaltung aus dem Link auf das Modell wartet. */
  function renderPendingPhoto() {
    pendingNote.hidden = !((state.pendingPhoto || state.pendingFrom) && !state.model)
  }

  /**
   * Foto aus dem Link fehlt oder lässt sich nicht öffnen: kurz sagen, was los ist. Der Konfigurator läuft normal weiter.
   * @param {{ error?: string, message?: string }} res
   */
  function linkPhotoProblem(res) {
    if (res.error === 'missing') toast.show({ title: 'Dein Foto ist nicht mehr da.', text: 'Lad es hier einfach nochmal hoch, dauert nur einen Moment.', tone: 'info' })
    else toast.show({ title: 'Das Foto ließ sich nicht öffnen.', text: res.message || 'Probier ein JPG, PNG oder WebP.', tone: 'error' })
  }

  /** Wartende Fotos prüfen und in den Verlauf nehmen (ohne Modell). Die Silhouette zeigt das erste schon. */
  async function preparePendingPhoto() {
    const key = state.pendingPhoto
    if (!key) return
    renderPendingPhoto()
    for (const extra of state.pendingPhotos) photo.adopt(extra, { apply: false }).catch(() => null)
    const res = await photo.adopt(key, { apply: false })
    // Inzwischen gewählt oder verworfen: nichts mehr zu tun
    if (state.pendingPhoto !== key) return
    if (res.error) {
      state.pendingPhoto = state.pendingPhotos.shift() || null
      renderPendingPhoto()
      if (!state.model) drawSilhouette()
      linkPhotoProblem(res)
      saveDraft()
      return
    }
    getPreview(key)
    announce('Dein Foto ist da. Wähl noch dein Handy, dann liegt es auf der Hülle.', 600)
    saveDraft()
  }

  /** Modell ist gewählt: wartendes Foto sofort auf die Hülle (füllt sie, Logos eingepasst), weitere als eigene Ebenen. */
  async function applyPendingPhoto() {
    const key = state.pendingPhoto
    if (!key || !state.model) return
    const extra = state.pendingPhotos.slice()
    state.pendingPhoto = null
    state.pendingPhotos = []
    renderPendingPhoto()
    const res = await photo.adopt(key, { apply: true })
    if (res.error) linkPhotoProblem(res)
    for (const k of extra) await photo.adopt(k, { apply: 'add' })
    saveDraft()
  }

  /**
   * Artikel zu "Gleiches Foto, anderes Handy": aus dem Warenkorb, im Prototyp auch aus der letzten Bestellung.
   * @param {string} id
   * @returns {Object | null}
   */
  function findSourceItem(id) {
    const fromCart = cartBackend.getCart().items.find((i) => i.id === id)
    if (fromCart) return fromCart
    const order = cartBackend.isShopify ? null : store.getOrder()
    return order && Array.isArray(order.items) ? order.items.find((i) => i.id === id) || null : null
  }

  /**
   * "Gleiches Foto, anderes Handy": die ganze Gestaltung (alle Foto-Ebenen mit Zuschnitt, Texte, Sticker, Grundfarbe)
   * auf das neu gewählte Handy legen. Positionen sind relativ, Fotos, die die Hülle gefüllt haben, füllen auch die neue.
   * @returns {boolean} true, wenn es geklappt hat
   */
  function applyPendingFrom() {
    const id = state.pendingFrom
    state.pendingFrom = null
    const item = id ? findSourceItem(id) : null
    if (!item || !item.config || !Array.isArray(item.config.layers)) return false
    const config = JSON.parse(JSON.stringify(item.config))
    delete config.modelId
    engine.load(config, { refit: true })
    state.pendingPhoto = null
    state.pendingPhotos = []
    renderPendingPhoto()
    refitDesign()
    for (const key of engine.getSrcKeys()) photo.adopt(key, { apply: false }).catch(() => null)
    announce(`Deine Gestaltung liegt jetzt auf der Hülle für das ${state.model.name}. Rück sie zurecht, wenn du magst.`, 300)
    return true
  }

  /* ------------------------------ Schritte ------------------------------ */

  function renderStepper() {
    stepper.innerHTML = STEP_LABELS.map((label, i) => {
      const n = i + 1
      const active = n === state.step
      const done = n < state.step
      const reachable = n === 1 || Boolean(state.model)
      const dot = done ? icon('check', { size: 14 }) : String(n)
      const cls = `k-stepper__step${active ? ' is-active' : ''}${done ? ' is-done' : ''}`
      const inner = `<span class="k-stepper__dot" aria-hidden="true">${dot}</span><span class="k-stepper__label">${label}</span>`
      if (active) return `<li class="${cls}" aria-current="step"><span class="k-cfg-stepbtn">${inner}<span class="k-visually-hidden">, Schritt ${n} von 3, aktuell</span></span></li>`
      if (!reachable) return `<li class="${cls}"><span class="k-cfg-stepbtn">${inner}<span class="k-visually-hidden">, Schritt ${n} von 3, erst Handy wählen</span></span></li>`
      return `<li class="${cls}"><button type="button" class="k-cfg-stepbtn" data-goto="${n}">${inner}<span class="k-visually-hidden">, Schritt ${n} von 3${done ? ', erledigt' : ''}</span></button></li>`
    }).join('')
  }

  stepper.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-goto]')
    if (btn) goToStep(Number(btn.dataset.goto))
  })

  /** Adresse zum aktuellen Zustand (ohne Namen und Fotos, die bleiben auf dem Gerät). */
  function stateUrl() {
    const p = new URLSearchParams()
    if (state.model) p.set('model', state.model.id)
    if (state.editId) p.set('edit', state.editId)
    if (debug) p.set('debug', '1')
    const q = p.toString()
    return `${location.pathname}${q ? `?${q}` : ''}`
  }

  /**
   * Schritt wechseln. Vorwärts kommt der neue Inhalt von rechts, zurück von links (motion.md 6).
   * @param {number} n
   * @param {{ push?: boolean, focus?: boolean, fromPop?: boolean }} [opts]
   */
  function goToStep(n, opts = {}) {
    let next = Math.max(1, Math.min(3, Number(n) || 1))
    if (next > 1 && !state.model) next = 1
    const prev = state.step
    state.step = next
    main.dataset.step = String(next)
    for (const p of stepPanels) {
      const on = Number(p.dataset.stepPanel) === next
      p.hidden = !on
      if (on && prev !== next && !state.booting) {
        p.style.setProperty('--dir', String(next > prev ? 1 : -1))
        p.classList.remove('is-entering')
        void p.offsetWidth
        p.classList.add('is-entering')
      }
    }
    renderStepper()
    renderHeader()
    updateChrome()
    const entry = { kCfg: 1, step: next }
    if (opts.fromPop) {
      // Browser hat die Adresse schon gesetzt
    } else if (opts.push !== false && prev !== next && !state.booting) history.pushState(entry, '', stateUrl())
    else history.replaceState(entry, '', stateUrl())
    if (prev !== next && !state.booting) {
      if (opts.focus !== false) {
        const heading = main.querySelector(`#k-cfg-step${next}-title`)
        if (heading) heading.focus({ preventScroll: true })
      }
      window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' })
      announce(`Schritt ${next} von 3: ${STEP_LABELS[next - 1]}`, 150)
    }
    if (next === 3) {
      tools.syncLayout()
      photoGallery.update({ immediate: true })
      collagePicker.update()
    }
    scheduleCompact()
    saveDraft()
  }

  window.addEventListener('popstate', (event) => {
    const s = event.state
    if (!s || !s.kCfg) return
    goToStep(s.step, { fromPop: true })
  })

  main.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-action]')
    if (!btn || !main.contains(btn)) return
    switch (btn.dataset.action) {
      case 'to-step-3':
        goToStep(3)
        break
      case 'add-photo':
        // Dateiauswahl sofort, solange der Klick noch als Nutzeraktion zählt
        photo.pick({ type: 'add' })
        break
      case 'add-name':
        toggleNameField()
        break
      case 'collage-off':
        if (engine.getConfig().collage) engine.setCollage(null)
        break
      case 'describe': {
        const open = describeEl.hidden
        describeEl.hidden = !open
        describeBtn.setAttribute('aria-expanded', String(open))
        if (open) describeEl.textContent = engine.describe()
        break
      }
      case 'shortcuts':
        openModal(keysModal, btn)
        break
      case 'draft-continue':
        hideDraftBar()
        main.querySelector(`#k-cfg-step${state.step}-title`)?.focus({ preventScroll: true })
        announce('Weiter geht es mit deinem Entwurf.')
        break
      case 'draft-reset':
        if (draftBar.dataset.kind === 'edit') startAnother()
        else if (draftBar.dataset.kind === 'link') startFromLink()
        else resetAll()
        break
      default:
        break
    }
  })

  /* ------------------------------ Kopf (Fokus-Header) ------------------------------ */

  const slotModel = getHeaderSlot('model')
  const slotSteps = getHeaderSlot('steps')
  const slotHistory = getHeaderSlot('history')
  // Unter 360 px wandern Rückgängig und Wiederholen an die Vorschau, damit der Modell-Chip lesbar bleibt
  const miniHistory = $('[data-cfg-history-mini]')
  const historyHosts = [slotHistory, miniHistory].filter(Boolean)

  for (const host of historyHosts) {
    host.innerHTML = `
      <button type="button" class="k-button k-button--icon k-cfg-history-btn" aria-label="Rückgängig" aria-disabled="true" data-history="undo">${icon('undo', { size: 22 })}</button>
      <button type="button" class="k-button k-button--icon k-cfg-history-btn" aria-label="Wiederholen" aria-disabled="true" data-history="redo">${icon('redo', { size: 22 })}</button>`
    host.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-history]')
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return
      if (btn.dataset.history === 'undo') engine.undo()
      else engine.redo()
    })
  }

  /** Kurzer Modellname für sehr schmale Köpfe: ohne Produktlinie, aber nur bei langen Namen. */
  // Kurzform für den Kopf, wenn der volle Name nicht passt (fitHeadChip): ohne Marke, "iPhone 16 Pro Max" -> "16 Pro Max"
  const shortModelName = (name) => name.replace(/^(iPhone|Galaxy|Pixel|Redmi)\s+/, '')
  if (slotModel) {
    slotModel.addEventListener('click', (event) => {
      if (event.target.closest('[data-action="change-model"]')) {
        goToStep(1)
        picker.focus()
      }
    })
  }

  function renderHeader() {
    if (slotModel) {
      slotModel.innerHTML = state.model
        ? `<button type="button" class="k-chip k-chip--truncate k-cfg-headchip" data-action="change-model" aria-label="${escapeHtml(state.model.name)}, Handy ändern">
            <span class="k-chip__text"><span class="k-cfg-headchip__full">${escapeHtml(state.model.name)}</span><span class="k-cfg-headchip__short">${escapeHtml(shortModelName(state.model.name))}</span><span class="k-cfg-headchip__extra"> · ändern</span></span></button>`
        : ''
    }
    if (slotSteps) slotSteps.innerHTML = `<span>Schritt ${state.step} von 3</span>`
    fitHeadChip()
    updateHistoryButtons()
  }

  /**
   * Modell-Chip im Kopf: Passt der volle Name nicht (schmale Handys, lange Namen), zeigt er die Kurzform ohne Marke.
   * Der volle Name steht immer im aria-label.
   */
  function fitHeadChip() {
    const chip = slotModel && slotModel.querySelector('.k-cfg-headchip')
    if (!chip) return
    chip.classList.remove('is-short')
    const text = chip.querySelector('.k-chip__text')
    if (text && text.scrollWidth > text.clientWidth + 1) chip.classList.add('is-short')
  }
  if (slotModel) {
    window.addEventListener('resize', debounce(fitHeadChip, 120))
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitHeadChip)
  }

  function updateHistoryButtons() {
    const canUndo = Boolean(state.model) && engine.canUndo()
    const canRedo = Boolean(state.model) && engine.canRedo()
    for (const host of historyHosts) {
      host.querySelector('[data-history="undo"]').setAttribute('aria-disabled', String(!canUndo))
      host.querySelector('[data-history="redo"]').setAttribute('aria-disabled', String(!canRedo))
    }
  }

  /* ------------------------------ Name dazu: ein freies Textfeld ------------------------------ */

  /**
   * Das Textfeld aus "Name dazu" (Rolle name), sonst null.
   * @param {Object} [c]
   */
  function nameLayer(c = engine.getConfig()) {
    return c.layers.find((l) => l.type === 'text' && l.role === 'name') || null
  }

  /** Namensplatz eines Looks (bind name), solange kein freies Feld da ist. "Name dazu" bearbeitet dann diesen. */
  function lookNameLayer(c = engine.getConfig()) {
    return c.layers.find((l) => l.type === 'text' && l.bind === 'name') || null
  }

  /** Wert für das Eingabefeld: Text des freien Felds oder der Name im Look. */
  function nameValueOf(layer, c = engine.getConfig()) {
    if (!layer) return ''
    return layer.bind === 'name' ? c.name || '' : layer.text || ''
  }

  function showNameLimit(on) {
    nameHint.hidden = !on
    if (on) {
      nameHint.innerHTML = `${icon('info', { size: 20 })}<span><span class="k-hint__label">Hinweis:</span> Mehr als ${LIMITS.textLayers} Texte passen nicht drauf. Lösch unter „Anpassen“ einen Text, dann ist wieder Platz.</span>`
      announce(`Hinweis: Mehr als ${LIMITS.textLayers} Texte passen nicht drauf.`)
    }
  }

  /**
   * Textfeld auf die Hülle legen (unteres Drittel, gut lesbar) oder das vorhandene öffnen. Kein Look, kein Rahmen.
   * @param {string} [text]
   * @param {{ focus?: boolean, select?: boolean }} [opts]
   * @returns {string | null} id der Ebene
   */
  function addNameField(text = '', opts = {}) {
    if (!state.model) return null
    const existing = nameLayer() || lookNameLayer()
    if (existing) {
      if (text && existing.bind === 'name') engine.setName(text)
      openNameEditor(engine.getLayer(existing.id) || existing, opts)
      return existing.id
    }
    const style = engine.textStyleAt(NAME_FIELD.x, NAME_FIELD.y)
    const id = engine.addText({ ...NAME_FIELD, text: Array.from(String(text)).slice(0, LIMITS.textChars).join(''), role: 'name', color: style.color, stroke: style.stroke })
    if (!id) {
      showNameLimit(true)
      return null
    }
    showNameLimit(false)
    openNameEditor(engine.getLayer(id), opts)
    announce(text ? `Textfeld mit „${text}“ liegt auf der Hülle.` : 'Textfeld liegt auf der Hülle. Tipp deinen Namen ein.', 150)
    return id
  }

  /**
   * Eingabe unter "Name dazu" öffnen und an das Textfeld binden.
   * @param {Object} layer
   * @param {{ focus?: boolean, select?: boolean }} [opts]
   */
  function openNameEditor(layer, opts = {}) {
    if (!layer) return
    state.nameOpen = true
    nameBody.hidden = false
    nameAddonBtn.setAttribute('aria-expanded', 'true')
    nameInput.dataset.layer = layer.id
    if (document.activeElement !== nameInput) nameInput.value = nameValueOf(layer)
    if (opts.select !== false) engine.select(layer.id)
    if (opts.focus !== false) {
      nameInput.focus({ preventScroll: true })
      nameInput.select()
      nameInput.scrollIntoView({ block: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' })
    }
  }

  function closeNameEditor() {
    state.nameOpen = false
    nameBody.hidden = true
    nameAddonBtn.setAttribute('aria-expanded', 'false')
  }

  function toggleNameField() {
    if (!state.model) {
      goToStep(1)
      picker.focus()
      return
    }
    if (state.nameOpen && (nameLayer() || lookNameLayer())) {
      closeNameEditor()
      return
    }
    addNameField('', { focus: true })
  }

  nameInput.addEventListener('input', () => {
    const id = nameInput.dataset.layer
    const layer = id ? engine.getLayer(id) : null
    if (!layer) {
      // Feld wurde inzwischen gelöscht: neu anlegen, der getippte Text bleibt
      const value = nameInput.value
      const fresh = addNameField(value, { focus: false, select: true })
      if (fresh) nameInput.dataset.layer = fresh
      return
    }
    // Name im Look: alle Namensplätze des Looks übernehmen ihn
    if (layer.bind === 'name') engine.setName(nameInput.value)
    else engine.updateLayer(id, { text: nameInput.value })
  })

  nameInput.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      nameInput.blur()
    }
  })

  /**
   * Text bearbeiten im Schritt 2: das Feld "Name dazu" direkt hier. Sonst false (dann übernimmt Schritt 3).
   * @param {Object} layer
   * @returns {boolean}
   */
  function editInline(layer) {
    if (!layer || (layer.role !== 'name' && layer.bind !== 'name') || state.step !== 2) return false
    openNameEditor(layer, { focus: true, select: false })
    return true
  }

  /** Text bearbeiten (Tipp auf gewählten Text, Doppelklick, Enter, "Ändern"). */
  function editText(layer) {
    if (editInline(layer)) return
    if (state.step !== 3) goToStep(3, { focus: false })
    tools.editText(layer)
  }

  /* ------------------------------ Hinweise unter der Vorschau ------------------------------ */

  function renderWarnings(list) {
    const shown = list.slice(0, 2)
    hintsEl.innerHTML = shown
      .map((w, i) => {
        let action = ''
        if (w.code === 'resolution' && w.level === 'bad') action = `<button type="button" class="k-button k-button--ghost k-hint__action" data-warn="${i}" data-warn-action="photo">Anderes Foto</button>`
        else if (w.code === 'unfilled') action = `<button type="button" class="k-button k-button--ghost k-hint__action" data-warn="${i}" data-warn-action="fill">Füllen</button>`
        else if (w.code === 'contrast') action = `<button type="button" class="k-button k-button--ghost k-hint__action" data-warn="${i}" data-warn-action="color">Farbe ändern</button>`
        else if (w.layerId && (w.code === 'margin' || w.code === 'camera' || w.code === 'tiny')) action = `<button type="button" class="k-button k-button--ghost k-hint__action" data-warn="${i}" data-warn-action="select">Zeigen</button>`
        return `<p class="k-hint k-cfg__hint">${icon(w.level === 'bad' ? 'alert' : 'info', { size: 20 })}<span><span class="k-hint__label">Hinweis:</span> ${escapeHtml(w.message)}</span>${action}</p>`
      })
      .join('')
    hintsEl.onclick = (event) => {
      const btn = event.target.closest('[data-warn]')
      if (!btn) return
      const w = shown[Number(btn.dataset.warn)]
      if (!w) return
      switch (btn.dataset.warnAction) {
        case 'photo': {
          const layer = w.layerId ? engine.getLayer(w.layerId) : null
          if (state.step !== 2 && !(layer && (layer.type === 'collage-slot' || layer.type === 'photo-slot'))) goToStep(2, { focus: false })
          photo.pick(layer)
          break
        }
        case 'fill':
          engine.fill(w.layerId)
          break
        case 'color':
          engine.select(w.layerId)
          if (state.step !== 3) goToStep(3, { focus: false })
          tools.openTool('color', { focus: true })
          break
        default:
          engine.select(w.layerId)
          break
      }
    }
    const keys = list.map((w) => `${w.code}:${w.layerId}`).join('|')
    if (keys !== lastWarningKeys) {
      const before = lastWarningKeys.split('|')
      const fresh = list.find((w) => !before.includes(`${w.code}:${w.layerId}`))
      if (fresh && !state.booting) announce(`Hinweis: ${fresh.message}`, 250)
      lastWarningKeys = keys
    }
  }

  /* ------------------------------ Engine-Ereignisse ------------------------------ */

  function onEngineChange(config) {
    if (!tools || !buy || !photo) return
    const designId = config.collage ? null : config.designId || LEGACY_PHOTO_DESIGN
    photoGallery.setSelected(config.collage ? null : designId)
    collagePicker.setSelected(config.collage ? config.collage.layout : null)
    if (collageOff) collageOff.hidden = !config.collage
    const keys = photoKeysForGallery(config).join('|')
    if (keys !== lastPhotoKeys) {
      lastPhotoKeys = keys
      photoGallery.update({ immediate: true })
      collagePicker.update()
    }
    // Karten der Looks zeigen den Namen aus "Name dazu" (gebündelt, nicht bei jedem Tastendruck neu)
    const galleryName = lookName(config)
    if (galleryName !== lastGalleryName) {
      lastGalleryName = galleryName
      photoGallery.update()
    }
    // Feld "Name dazu" folgt der Hülle (Rückgängig, Löschen, Look mit Namensplatz, Schritt 3)
    const nl = nameLayer(config) || lookNameLayer(config)
    if (!nl && state.nameOpen) closeNameEditor()
    const value = nameValueOf(nl, config)
    if (nl && document.activeElement !== nameInput && nameInput.value !== value) nameInput.value = value
    if (nl) nameInput.dataset.layer = nl.id
    tools.refresh()
    photo.refresh(config)
    buy.onConfigChange()
    updateHistoryButtons()
    if (!describeEl.hidden) describeEl.textContent = engine.describe()
    saveDraft()
  }

  function onEngineSelect(sel) {
    if (!tools || !photo) return
    tools.onSelect(sel)
    photo.refresh(engine.getConfig())
  }

  /* ------------------------------ Bühne und Silhouette ------------------------------ */

  function showStage(on) {
    stageEl.hidden = !on
    silhouetteEl.hidden = on
    contextEl.hidden = !on
    belowEl.hidden = !on
    if (!on) drawSilhouette()
  }

  /** Vorschau ohne Modell: neutrale Silhouette, oder schon das Foto aus dem Link auf einer allgemeinen Hülle. */
  function drawSilhouette() {
    const h = stageBox.clientHeight || 280
    const w = stageBox.clientWidth || 200
    const width = Math.max(90, Math.min(w * 0.7, h * 0.46))
    const waiting = state.pendingPhoto ? previews.get(state.pendingPhoto) : null
    silhouetteEl.innerHTML = ''
    if (waiting && waiting.bitmap) {
      const canvas = renderPhotoMockup({ photo: waiting.bitmap, model: null, width })
      canvas.classList.add('k-cfg__silhouette-photo')
      silhouetteEl.append(canvas)
    } else silhouetteEl.append(renderSilhouette({ width }))
  }

  /* ------------------------------ Layout: Viewport, Tastatur, Kaufleiste ------------------------------ */

  function updateChrome() {
    const showBuy = state.step >= 2 && Boolean(state.model)
    buyEl.hidden = !showBuy
    ctaEl.hidden = !showBuy
    const mobileBar = showBuy && !desktopMq.matches
    ctaEl.classList.toggle('is-tucked', mobileBar && state.keyboardOpen)
    document.body.classList.toggle('has-sticky-bar', mobileBar && !state.keyboardOpen)
  }

  const vv = window.visualViewport
  function onViewport() {
    const h = vv ? vv.height : window.innerHeight
    document.documentElement.style.setProperty('--cfg-vvh', `${Math.round(h)}px`)
    state.keyboardOpen = window.innerHeight - h > 150
    updateChrome()
    scheduleCompact()
  }

  /* ------------------------------ Mobil: Vorschau wird kleiner, sobald das Panel darunter durchscrollt ------------------------------ */

  // Zwei unsichtbare Messlatten liefern die volle und die kleine Höhe der Vorschau (beide aus configurator.css)
  const probeFull = document.createElement('div')
  const probeCompact = document.createElement('div')
  probeFull.className = 'k-cfg__probe k-cfg__probe--full'
  probeCompact.className = 'k-cfg__probe k-cfg__probe--compact'
  probeFull.setAttribute('aria-hidden', 'true')
  probeCompact.setAttribute('aria-hidden', 'true')
  main.append(probeFull, probeCompact)

  let previewCompact = false
  let compactFrame = 0

  /**
   * Klein wird die Vorschau erst, wenn mindestens so viel Panel unter ihr liegt, wie sie schrumpft: Dann deckt sie
   * genau diesen Teil wieder auf, nichts springt und es entsteht keine Lücke. Höhe und Abstand darunter ändern sich
   * gemeinsam (configurator.css), das Layout darunter bleibt stehen. Zurück auf groß knapp darunter (Hysterese).
   */
  function updateCompact() {
    compactFrame = 0
    let next = false
    if (!desktopMq.matches && state.step >= 2 && state.model) {
      const shrink = Math.max(0, probeFull.offsetHeight - probeCompact.offsetHeight)
      // Leiste und Kopf kleben oben, die Vorschau klebt direkt darunter
      const y = window.scrollY
      if (shrink > 24) next = previewCompact ? y > shrink - 12 : y > shrink + 12
    }
    if (next === previewCompact) return
    previewCompact = next
    main.classList.toggle('is-preview-compact', next)
  }

  function scheduleCompact() {
    if (!compactFrame) compactFrame = requestAnimationFrame(updateCompact)
  }
  window.addEventListener('scroll', scheduleCompact, { passive: true })
  if (vv) vv.addEventListener('resize', onViewport)
  window.addEventListener('resize', onViewport)
  onViewport()

  if (typeof ResizeObserver === 'function') {
    new ResizeObserver(() => {
      document.documentElement.style.setProperty('--cfg-stick-h', `${Math.round(stick.getBoundingClientRect().height)}px`)
      if (!state.model) drawSilhouette()
    }).observe(stick)
  }

  /** Zusatz unter der Vorschau: ab 1024 px an der Bühne, mobil im Panel vor der Kaufbox (die Vorschau klebt dort oben). */
  function placeAside() {
    const target = desktopMq.matches ? stick : asideSlot
    if (asideEl.parentElement !== target) target.append(asideEl)
    // Hinweise: ab 1024 px direkt unter der Bühne, mobil oben im Panel, damit die klebende Vorschau ihre Größe behält
    const hintTarget = desktopMq.matches ? stick : hintsSlot
    if (hintsEl.parentElement !== hintTarget) {
      if (hintTarget === stick) stick.insertBefore(hintsEl, asideEl.parentElement === stick ? asideEl : null)
      else hintTarget.append(hintsEl)
    }
  }
  placeAside()

  desktopMq.addEventListener('change', () => {
    placeAside()
    tools.syncLayout()
    updateChrome()
    scheduleCompact()
  })

  // Erste Berührung der Hülle: der Hinweis "Tipp auf …" hat seinen Job erledigt
  stageEl.addEventListener('pointerdown', () => tools.dismissHint(), { passive: true })

  /* ------------------------------ Tastatur: ? und Entf ------------------------------ */

  const ownsKeys = (el) => {
    if (!el || typeof el.closest !== 'function') return false
    if (el.isContentEditable) return true
    if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true
    if (el.tagName === 'INPUT') return !['button', 'submit', 'reset', 'checkbox', 'file', 'image', 'color'].includes((el.getAttribute('type') || 'text').toLowerCase())
    return Boolean(el.closest('[role="radiogroup"], [role="listbox"], [role="combobox"], [role="slider"], [role="tablist"], [role="menu"], [role="grid"]'))
  }

  main.addEventListener(
    'keydown',
    (event) => {
      if (event.defaultPrevented || event.isComposing || ownsKeys(event.target)) return
      if (event.key === '?' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault()
        openModal(keysModal, document.activeElement)
        return
      }
      if ((event.key === 'Delete' || event.key === 'Backspace') && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const sel = engine.getSelected()
        if (!sel) return
        event.preventDefault()
        event.stopPropagation()
        tools.deleteTarget(sel.id)
      }
    },
    true,
  )

  /* ------------------------------ Modale Infos ------------------------------ */

  /**
   * Natives dialog als Modal: Fokus auf die Überschrift, Esc schließt, Fokus zurück (a11y 3.4).
   * @param {HTMLDialogElement} dialog
   * @param {Element | null} opener
   */
  function openModal(dialog, opener) {
    if (!dialog || dialog.open) return
    dialog.showModal()
    document.documentElement.classList.add('k-scroll-locked')
    const title = dialog.querySelector('.k-modal__title')
    if (title) title.focus({ preventScroll: true })
    dialog.addEventListener(
      'close',
      () => {
        document.documentElement.classList.remove('k-scroll-locked')
        if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true })
      },
      { once: true },
    )
  }

  for (const dialog of [magsafeModal, keysModal]) {
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-action="modal-close"]')) dialog.close()
    })
  }

  /* ------------------------------ Entwurf ------------------------------ */

  let storageWarned = false
  /** Stand der Hülle beim letzten "In den Warenkorb" (dann wird der Entwurf nicht mehr angeboten). */
  let addedSig = null
  const configSig = (c, magsafe) => JSON.stringify({ c, m: Boolean(magsafe) })

  function draftData() {
    return {
      v: 1,
      config: engine.getConfig(),
      modelId: state.model ? state.model.id : null,
      mode: 'photo',
      magsafe: state.magsafe,
      step: state.step,
      editId: state.editId,
      addedSig,
      uploads: state.uploads,
      recentModels: state.recentModels,
      modelRequests: state.modelRequests,
      // Fotos und Gestaltung aus dem Link, die noch auf das Modell warten (hält sie auch beim Aufräumen von IndexedDB fest)
      pendingPhoto: state.pendingPhoto,
      pendingPhotos: state.pendingPhotos,
      pendingFrom: state.pendingFrom,
      hintDismissed: state.hintDismissed,
      savedAt: Date.now(),
    }
  }

  const saveDraft = debounce(() => {
    if (state.booting) return
    // Adresse folgt dem Zustand (ohne neuen Verlaufseintrag)
    if (history.state && history.state.kCfg) history.replaceState(history.state, '', stateUrl())
    const data = draftData()
    store.setDraft(data)
    const back = store.getDraft()
    if ((!back || back.savedAt !== data.savedAt) && !storageWarned) {
      storageWarned = true
      storageHint.hidden = false
      announce('Hinweis: Dein Browser speichert gerade nichts zwischen. Bleib auf der Seite, bis du fertig bist.')
    }
  }, 300)

  // Letzte Eingabe nicht verlieren, wenn jemand die Seite schnell verlässt (RESEARCH 9 Punkt 13)
  window.addEventListener('pagehide', () => saveDraft.flush())
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) saveDraft.flush()
  })

  function markDraftAdded() {
    addedSig = configSig(engine.getConfig(), state.magsafe)
    saveDraft()
  }

  function hideDraftBar() {
    draftBar.hidden = true
  }

  /**
   * Leiste oben im Panel: Entwurf (Weitermachen oder neu anfangen) oder Bearbeiten aus dem Warenkorb.
   * @param {string} text
   * @param {'draft' | 'edit' | 'link'} [kind='draft'] link: Entwurf geladen, der Link aus der Adresse wartet
   */
  function showDraftBar(text, kind = 'draft') {
    draftBar.hidden = false
    draftBar.dataset.kind = kind
    draftText.textContent = text
    draftBar.querySelector('[data-action="draft-continue"]').textContent = kind === 'edit' ? 'Alles klar' : 'Weitermachen'
    const linkLabel = photoParam || fromParam ? 'Mit neuem Foto anfangen' : 'Mit dem Link neu anfangen'
    draftBar.querySelector('[data-action="draft-reset"]').textContent = kind === 'edit' ? 'Lieber neue Hülle' : kind === 'link' ? linkLabel : 'Neu anfangen'
  }

  /** Neu anfangen: alles zurück, Listen (Verlauf, zuletzt gewählt) bleiben. */
  function resetAll() {
    resetState()
    goToStep(1, { push: false })
    main.querySelector('#k-cfg-step1-title').focus({ preventScroll: true })
    announce('Alles auf Anfang. Welches Handy bekommt die Hülle?')
  }

  /** Zustand ohne Schrittwechsel zurücksetzen (für "Neu anfangen" und den Start aus einem Link). */
  function resetState() {
    hideDraftBar()
    state.model = null
    state.magsafe = false
    state.editId = null
    state.pending = null
    state.pendingPhoto = null
    state.pendingPhotos = []
    state.pendingFrom = null
    addedSig = null
    engine.setModel(null)
    engine.load(emptyConfig())
    nameInput.value = ''
    closeNameEditor()
    showNameLimit(false)
    picker.setSelected(null)
    picker.refreshRecent()
    renderPendingPhoto()
    showStage(false)
    buy.reset()
  }

  /** Entwurf verwerfen und so starten, wie der Link es vorgibt (?model=&name=&photo=&from=). */
  function startFromLink() {
    // Neues Foto aus dem Link ohne Modell: das Handy aus dem Entwurf passt fast immer weiter (nicht bei "anderes Handy")
    const keepModel = photoParam && !fromParam && !params.get('model') ? state.model : null
    resetState()
    startFromUrl()
    if (keepModel && !state.model) {
      selectModel(keepModel)
      goToStep(2, { push: false, focus: false })
    }
    saveDraft()
    main.querySelector(`#k-cfg-step${state.step}-title`)?.focus({ preventScroll: true })
    announce(state.model ? `Neu angefangen mit ${state.model.name}.` : 'Neu angefangen. Welches Handy bekommt die Hülle?')
  }

  /** Nach dem Kauf: gleiches Handy, neue leere Hülle (Duo), die letzten Fotos liegen im Verlauf. */
  function startAnother() {
    state.editId = null
    state.magsafe = false
    addedSig = null
    engine.load(emptyConfig())
    nameInput.value = ''
    closeNameEditor()
    showNameLimit(false)
    buy.reset()
    hideDraftBar()
    goToStep(2, { push: state.step !== 2 })
    history.replaceState(history.state, '', stateUrl())
    main.querySelector('[data-action="upload"]').focus()
    announce('Neue Hülle, gleiches Handy. Lad das nächste Foto hoch, deine letzten Fotos liegen im Verlauf.')
  }

  /**
   * Entwurf übernehmen. Alte Entwürfe (Namens-Designs, "Nur mein Foto") laufen im Foto-Weg weiter: Die Engine
   * macht aus dem alten Hauptfoto eine freie Foto-Ebene, Namens-Designs bleiben als Ebenen erhalten.
   * @param {Object} d
   * @param {{ step?: number }} [opts]
   */
  async function restoreDraft(d, opts = {}) {
    const model = d.modelId ? modelsById.get(d.modelId) : null
    state.magsafe = Boolean(d.magsafe)
    state.editId = d.editId || null
    addedSig = d.addedSig || null
    if (model) {
      state.model = model
      picker.setSelected(model)
      showStage(true)
      engine.setModel(model)
      await engine.load(d.config || {}).catch(() => null)
      goToStep(opts.step || d.step || 2, { push: false })
    } else {
      // Fotos oder eine Gestaltung aus dem Link warteten noch auf das Modell: sie warten weiter
      if (typeof d.pendingPhoto === 'string' && PHOTO_KEY.test(d.pendingPhoto)) {
        state.pendingPhoto = d.pendingPhoto
        state.pendingPhotos = Array.isArray(d.pendingPhotos) ? d.pendingPhotos.filter((k) => typeof k === 'string' && PHOTO_KEY.test(k)).slice(0, LIMITS.photos - 1) : []
        preparePendingPhoto()
      }
      if (typeof d.pendingFrom === 'string' && ITEM_KEY.test(d.pendingFrom)) state.pendingFrom = d.pendingFrom
      renderPendingPhoto()
      goToStep(1, { push: false })
    }
  }

  /**
   * Bearbeiten aus dem Warenkorb: Artikel laden, Button "Änderungen speichern".
   * @param {Object} item CartItem
   */
  async function startEdit(item) {
    const model = modelsById.get(item.modelId)
    if (!model || !item.config) return false
    state.model = model
    state.editId = item.id
    state.magsafe = Boolean(item.magsafe)
    picker.setSelected(model)
    showStage(true)
    engine.setModel(model)
    await engine.load(item.config).catch(() => null)
    goToStep(2, { push: false })
    const editNote = `Du änderst die Hülle für ${model.name} aus deinem Warenkorb. Speichern ersetzt sie dort.`
    showDraftBar(editNote, 'edit')
    // Fotos liegen nur auf dem Gerät, auf dem gestaltet wurde (IndexedDB). Fehlen sie hier, gleich sagen.
    const keys = engine.getSrcKeys()
    if (keys.length) {
      const found = await Promise.all(keys.map((key) => getFile(key).catch(() => null)))
      if (found.some((blob) => !blob) && state.editId === item.id) {
        showDraftBar(`${editNote} Deine Fotos liegen aber auf einem anderen Gerät: Lad sie hier nochmal hoch, bevor du speicherst.`, 'edit')
      }
    }
    return true
  }

  /**
   * Start aus Deep Link (?model=&name=&photo=&from=). Immer der Foto-Weg; ?mode= und ?design= alter Links zählen
   * nicht mehr. Fotos aus dem Link warten ohne Modell in Schritt 1 und liegen nach der Modellwahl sofort auf der Hülle.
   */
  function startFromUrl() {
    const model = params.get('model') ? modelsById.get(params.get('model')) : null
    const name = sanitizeName(params.get('name') || '')
    state.pending = name ? { name } : null
    state.pendingPhoto = photoParam
    state.pendingPhotos = photoParams.slice(1)
    state.pendingFrom = fromParam
    if (model) {
      selectModel(model, { silent: true })
      goToStep(2, { push: false })
    } else {
      goToStep(1, { push: false })
      if (state.pendingPhoto) preparePendingPhoto()
      renderPendingPhoto()
      if (params.get('model')) announce('Das Handy aus dem Link kennen wir nicht. Such es hier aus.')
    }
  }

  /* ------------------------------ Startentscheidung ------------------------------ */

  renderStepper()
  renderHeader()
  showStage(false)

  const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null
  const returning = Boolean((history.state && history.state.kCfg) || (nav && (nav.type === 'reload' || nav.type === 'back_forward')))
  const editParam = params.get('edit')
  // ?mode= und ?design= alter Links tragen nichts mehr bei: Sie allein sind kein Link mit Inhalt
  const hasDeepLink = ['model', 'name'].some((k) => params.has(k)) || Boolean(photoParam) || Boolean(fromParam)
  /** Fotos aus dem Link landen in jedem Fall im Verlauf, auf die Hülle aber nur über startFromUrl. */
  let linkPhotoUsed = false
  const draftUsable = draft && draft.v === 1 && draft.config
  /** Entwurf mit Inhalt, der noch nicht so im Warenkorb liegt. */
  const draftPending = Boolean(draftUsable && draft.modelId && modelsById.has(draft.modelId) && hasContent(draft.config) && configSig(draft.config, draft.magsafe) !== draft.addedSig)

  if (editParam) {
    if (returning && draftUsable && draft.editId === editParam) await restoreDraft(draft, { step: history.state && history.state.step })
    else {
      let item = cartBackend.getCart().items.find((i) => i.id === editParam)
      // Shopify: Der Stand aus Liquid kann älter sein als der Warenkorb, einmal frisch holen
      if (!item && cartBackend.isShopify) {
        const fresh = await cartBackend.refresh().catch(() => null)
        item = fresh ? fresh.items.find((i) => i.id === editParam) : null
      }
      const ok = item ? await startEdit(item) : false
      if (!ok) {
        toast.show({ title: 'Die Hülle liegt nicht mehr im Warenkorb.', text: 'Gestalte einfach eine neue, dauert drei Minuten.', tone: 'info' })
        goToStep(1, { push: false })
      }
    }
  } else if (returning && draftUsable && (!params.get('model') || params.get('model') === draft.modelId)) {
    await restoreDraft(draft, { step: history.state && history.state.step })
    if (draft.editId) {
      const model = modelsById.get(draft.modelId)
      if (model) showDraftBar(`Du änderst die Hülle für ${model.name} aus deinem Warenkorb. Speichern ersetzt sie dort.`, 'edit')
    }
  } else if (hasDeepLink) {
    if (draftPending) {
      // Ein ungesicherter Entwurf wird nie still vom Link überschrieben (RESEARCH 3.4): erst fragen
      await restoreDraft(draft, { step: Math.max(2, Number(draft.step) || 2) })
      const model = modelsById.get(draft.modelId)
      const which = model ? model.name : 'dein Handy'
      showDraftBar(
        photoParam || fromParam
          ? `Du hast noch einen Entwurf für ${which}. Weitermachen oder mit deinem neuen Foto anfangen? Das Foto liegt so oder so in deinem Verlauf.`
          : `Du hast einen Entwurf für ${which}. Weitermachen oder mit dem Link neu anfangen?`,
        'link',
      )
    } else {
      startFromUrl()
      linkPhotoUsed = true
    }
  } else if (draftPending) {
    await restoreDraft(draft, { step: Math.max(2, Number(draft.step) || 2) })
    const model = modelsById.get(draft.modelId)
    if (model) showDraftBar(`Du hast einen Entwurf für ${model.name}. Weitermachen oder neu anfangen?`)
  } else {
    goToStep(1, { push: false })
  }

  // Fotos aus dem Link, die (noch) nicht auf die Hülle kamen: zumindest im Verlauf anbieten
  if (photoParams.length && !linkPhotoUsed) {
    for (const key of photoParams) {
      photo.adopt(key, { apply: false }).then((res) => {
        if (res.error && key === photoParam) linkPhotoProblem(res)
      })
    }
  }

  state.booting = false
  // Ohne ?photo= und andere Einmal-Parameter: die Adresse beschreibt ab hier nur noch den Zustand
  history.replaceState({ kCfg: 1, step: state.step }, '', stateUrl())
  updateChrome()
  renderHeader()
  onEngineChange(engine.getConfig())
  tools.onSelect(engine.getSelected())
  photoGallery.update({ immediate: true })
  collagePicker.update()
  if (!draftBar.hidden) draftText.focus({ preventScroll: true })

  if (debug) window.__keepsyConfigurator = { engine, state, goToStep, selectModel, store, addNameField }
}
